<template>
    <div class="kanban">
        <div v-if="erro" class="kanban__erro">
            <p>{{ erro }}</p>
            <Button icon="pi pi-refresh" label="Tentar de novo" severity="warn" size="small" @click="carregar" />
        </div>

        <p v-else-if="carregandoBoard" class="kanban__estado" role="status" aria-live="polite">
            <i class="pi pi-spin pi-spinner" aria-hidden="true"></i> Carregando quadro de contratos…
        </p>

        <div v-else class="kanban__colunas">
            <KanbanColuna
                v-for="coluna in colunas"
                :key="coluna.id"
                :coluna="coluna"
                :carregando="!!carregandoColunas[coluna.id]"
                @carregar-mais="carregarMais(coluna)"
                @ordenar="ordenar(coluna, $event)"
                @abrir="$emit('abrir', $event)"
            />
        </div>
    </div>
</template>

<script>
import Button from 'primevue/button';
import KanbanColuna from './KanbanColuna.vue';
import Kanban from '@/service/contratoKanban.js';

const LIMITE = 20;

/**
 * Quadro de contratos por etapa (T5.2), com paginação por cursor em cada
 * coluna (T5.4) e ordenação por coluna (T5.5).
 *
 * Cada coluna guarda o próprio `pagina.proximo_cursor` dentro do próprio
 * objeto — não há estado de paginação central, e rolar uma coluna não encosta
 * nas outras. Trocar a ordenação recomeça a coluna sem cursor, porque o cursor
 * cifrado carrega a ordenação dentro dele e o backend ignora `ordenar_por`
 * quando ele vem junto.
 */
export default {
    name: 'KanbanBoard',
    components: { Button, KanbanColuna },
    emits: ['abrir', 'colunas'],
    data() {
        return {
            colunas: [],
            // Teto de permissão do usuário, para o seletor de colunas: vem em toda
            // resposta (inclusive `?etapa=`) e NÃO encolhe com a preferência, ao
            // contrário de `colunas`, que é permissão ∩ preferência.
            permitidas: [],
            carregandoColunas: {},
            carregandoBoard: true,
            erro: null,
        };
    },
    mounted() {
        this.carregar();
    },
    methods: {
        carregar() {
            this.carregandoBoard = true;
            this.erro = null;

            Kanban.listar(`limite=${LIMITE}`)
                .then(({ data }) => {
                    this.colunas = data.data.colunas;
                    this.permitidas = data.data.colunas_permitidas || [];
                    this.emitirColunas();
                })
                .catch(erro => {
                    this.erro = this.mensagem(erro);
                })
                .finally(() => {
                    this.carregandoBoard = false;
                });
        },
        carregarMais(coluna) {
            if (this.carregandoColunas[coluna.id] || !coluna.pagina.tem_mais) return;

            this.buscarColuna(
                coluna,
                `cursor=${encodeURIComponent(coluna.pagina.proximo_cursor)}`,
                true
            );
        },
        ordenar(coluna, valor) {
            const [campo, direcao] = valor.split(':');
            if (campo === coluna.ordenacao.campo && direcao === coluna.ordenacao.direcao) return;

            this.buscarColuna(coluna, `ordenar_por=${campo}&direcao=${direcao}`, false);
        },
        /** `acrescentar` distingue rolar (anexa a página) de reordenar (recomeça). */
        buscarColuna(coluna, extra, acrescentar) {
            this.carregandoColunas[coluna.id] = true;

            Kanban.listar(`etapa=${coluna.id}&limite=${LIMITE}&${extra}`)
                .then(({ data }) => {
                    this.permitidas = data.data.colunas_permitidas || [];
                    const nova = data.data.colunas[0];
                    const indice = this.colunas.findIndex(item => item.id === coluna.id);
                    if (!nova || indice < 0) return;

                    this.colunas[indice] = acrescentar
                        ? { ...nova, cards: [...coluna.cards, ...nova.cards] }
                        : nova;

                    if (!acrescentar) this.emitirColunas();
                })
                .catch(erro => {
                    // Cursor recusado (expirado, tenant trocado, adulterado) é
                    // erro que se repetiria a cada pixel rolado. Fecha a coluna
                    // em vez de bater no servidor em laço.
                    if (acrescentar) coluna.pagina.tem_mais = false;

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Ops!',
                        detail: `${coluna.descricao}: ${this.mensagem(erro)}`,
                        life: 5000,
                    });
                })
                .finally(() => {
                    this.carregandoColunas[coluna.id] = false;
                });
        },
        emitirColunas() {
            this.$emit('colunas', {
                colunas: this.colunas.map(({ id, ordenacao }) => ({ id, ordenacao })),
                permitidas: this.permitidas,
            });
        },
        mensagem(erro) {
            const dados = erro.response?.data;
            return dados?.msg
                || dados?.message
                || Object.values(dados?.errors || {})[0]?.[0]
                || erro.message;
        },
    },
};
</script>

<style scoped>
.kanban__colunas {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 8px;
    scroll-snap-type: x proximity;
}
.kanban__estado,
.kanban__erro {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 200px;
    color: var(--surface-600);
}
.kanban__erro {
    flex-direction: column;
    text-align: center;
}
</style>
